import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { offlineCache } from '../services/offlineCache';
import { offlineRequestsKeys } from './queries/useOfflineRequestsQuery';
import { requestsKeys } from './queries/useRequestsQuery';
import { normalizeStage } from '../types/stages';

/**
 * Hook to get request stage immediately for App-level sidebar management
 * Uses React Query cache first, then falls back to offline cache
 */
export const useRequestStage = (requestId: string | null, enabled: boolean = true) => {
  const [stage, setStage] = useState<string>('DRAFT');
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !requestId) {
      setStage('DRAFT');
      setIsLoading(false);
      return;
    }

    const loadStage = async () => {
      setIsLoading(true);
      try {
        // Try multiple cache sources in priority order
        let request = null;

        // 1. Try offline individual cache first (most up-to-date)
        request = queryClient.getQueryData(offlineRequestsKeys.detail(requestId));

        // 2. If not found, try offline list cache
        if (!request) {
          const allRequestsData = queryClient.getQueryData(offlineRequestsKeys.lists());
          if (allRequestsData && Array.isArray(allRequestsData)) {
            request = allRequestsData.find(req => req.id === requestId);
          }
        }

        // 3. Try online individual cache
        if (!request) {
          request = queryClient.getQueryData(requestsKeys.detail(requestId));
        }

        // 4. Try online list cache
        if (!request) {
          const allRequestsData = queryClient.getQueryData(requestsKeys.lists());
          if (allRequestsData && Array.isArray(allRequestsData)) {
            request = allRequestsData.find(req => req.id === requestId);
          }
        }

        // 5. Pending offline creates (o-*) — stored in offline queue, not server cache
        if (!request && requestId.startsWith('o-')) {
          const offlineEntry = await offlineCache.getOfflineRequestById(requestId);
          if (offlineEntry) {
            const data = offlineEntry.data as Record<string, unknown>;
            const rawStage =
              (typeof data.stage === 'string' && data.stage) ||
              (typeof data.sysRequestStage === 'string' && data.sysRequestStage);
            request = {
              id: offlineEntry.id,
              stage: rawStage || 'DRAFT',
              sysRequestStage:
                typeof data.sysRequestStage === 'string' ? data.sysRequestStage : undefined,
            };
          }
        }

        // 6. Fallback to offline storage for requests not in React Query cache
        if (!request) {
          request = await offlineCache.getCachedRequestById(requestId);
        }

        if (request) {
          const rawStage = request.stage || request.sysRequestStage;
          const normalizedStage = rawStage ? normalizeStage(rawStage) : 'DRAFT';
          setStage(normalizedStage || 'DRAFT');
        } else {
          setStage('DRAFT');
        }
      } catch (error) {
        console.error('Failed to load request stage:', error);
        setStage('DRAFT');
      } finally {
        setIsLoading(false);
      }
    };

    loadStage();

    // Reduced frequency subscription - only listen to our specific request caches
    let timeoutId: ReturnType<typeof setTimeout>;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' || event.type === 'added') {
        const query = event.query;
        const isRelevantUpdate =
          JSON.stringify(query.queryKey) === JSON.stringify(offlineRequestsKeys.detail(requestId)) ||
          JSON.stringify(query.queryKey) === JSON.stringify(requestsKeys.detail(requestId)) ||
          (JSON.stringify(query.queryKey) === JSON.stringify(offlineRequestsKeys.lists()) &&
           query.state.data && Array.isArray(query.state.data) &&
           query.state.data.some((req: any) => req.id === requestId)) ||
          (JSON.stringify(query.queryKey) === JSON.stringify(requestsKeys.lists()) &&
           query.state.data && Array.isArray(query.state.data) &&
           query.state.data.some((req: any) => req.id === requestId));

        if (isRelevantUpdate) {
          // Debounce rapid cache updates to prevent loops
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            loadStage();
          }, 100);
        }
      }
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [requestId, enabled, queryClient]);

  return { stage, isLoading };
};